import clsx from "clsx"
import { forwardRef } from "react"

import { faIcon } from "@utils/faIcon"

import Skeleton from "@particles/Skeleton/Skeleton"

import { Icon, NavigationLink } from "@atoms"
import toggleStyles from "@atoms/Toggle/Toggle.module.scss"

import styles from "./MenuItem.module.scss"
import { MenuItemProps, ToggleIndicatorProps } from "./MenuItem.types"

// The row owns the switch semantics, so the switch itself only shows the value — the same
// bare track ToggleListItem draws.
function ToggleIndicator(props: ToggleIndicatorProps) {
    const { isChecked } = props

    const dataState = isChecked ? "checked" : "unchecked"

    return (
        <div className={clsx(toggleStyles.base, styles.toggle)} data-state={dataState} aria-hidden="true">
            <div className={toggleStyles.toggleWrapper}>
                <div className={toggleStyles.toggle}>
                    <div>
                        <span className={toggleStyles.thumb} data-state={dataState} />
                    </div>
                </div>
            </div>
        </div>
    )
}

function MenuItem(props: MenuItemProps, ref: React.Ref<HTMLAnchorElement>) {
    const {
        variant = "default",
        className,
        icon,
        iconActive,
        avatar,
        rightIcon,
        showSubMenu,
        toggle,

        path,

        title,
        caption,
        tag,

        onClick,
        onActive,

        isDisabled = false,
        isActive = undefined,
        isLoading,
        _isFocused,
        _isHovered,
        isPressed,
        ...rest
    } = props

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (isDisabled) {
            event.preventDefault()
        } else {
            toggle?.onCheckedChange(!toggle.isChecked)
            onClick?.(event)
        }
    }

    return (
        <NavigationLink
            title={title}
            path={path}
            className={clsx(styles.base, className, styles[`${variant}Variant`], {
                [styles.disabled]: isDisabled,
                [styles.loading]: isLoading,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
            })}
            activeClassName={styles.active}
            onClick={handleClick}
            isActive={isActive}
            onActive={onActive}
            isDisabled={isDisabled}
            role={toggle ? "menuitemcheckbox" : undefined}
            aria-checked={toggle ? toggle.isChecked : undefined}
            ref={ref}
            {...rest}
        >
            {(isActive) => {
                if (isLoading) {
                    return (
                        <>
                            <div className={styles.body}>
                                {(avatar || icon) && (
                                    <div className={avatar ? styles.avatar : styles.icon}>
                                        <Skeleton circle={true} width={avatar ? 20 : 16} height={avatar ? 20 : 16} />
                                    </div>
                                )}
                                <span className={styles.copy}>
                                    <Skeleton width="100%" height={10} borderRadius={0} />
                                    {caption !== undefined && <Skeleton width="100%" height={10} borderRadius={0} />}
                                </span>
                            </div>
                            {tag !== undefined && (
                                <span className={styles.tag}>
                                    <Skeleton width={48} height={10} borderRadius={0} />
                                </span>
                            )}
                            {toggle && (
                                <div className={styles.toggle}>
                                    <Skeleton width={32} height={20} borderRadius={24} />
                                </div>
                            )}
                            {(showSubMenu || rightIcon) && (
                                <div className={styles.rightIcon}>
                                    <Skeleton circle={true} width={16} height={16} />
                                </div>
                            )}
                        </>
                    )
                } else {
                    return (
                        <>
                            <div className={styles.body}>
                                {avatar ? (
                                    <div className={styles.avatar}>{avatar}</div>
                                ) : (
                                    icon && (
                                        <Icon
                                            icon={icon}
                                            iconOnActive={iconActive}
                                            isActive={isActive}
                                            containerClassName={styles.icon}
                                            container="square"
                                            size={14}
                                        />
                                    )
                                )}
                                <span className={styles.copy}>
                                    <span className={styles.title}>{title}</span>
                                    {caption !== undefined && <span className={styles.caption}>{caption}</span>}
                                </span>
                            </div>
                            {tag !== undefined && <span className={styles.tag}>{tag}</span>}
                            {toggle && <ToggleIndicator isChecked={toggle.isChecked} />}
                            {(showSubMenu || rightIcon) && (
                                <Icon
                                    icon={rightIcon ?? faIcon({ name: "angle-right", style: "regular" })}
                                    containerClassName={styles.rightIcon}
                                    container="square"
                                    size={14}
                                />
                            )}
                        </>
                    )
                }
            }}
        </NavigationLink>
    )
}

export default forwardRef(MenuItem)
